import type { ReactNode } from 'react';
import './funnel.css';

export interface FunnelStep {
  label: string;
  value: number;
  /** The same step in the period before: an outline behind the bar. */
  previous?: number;
  /** Where the step's name leads: its companies. */
  href?: string;
}

/** What a bar's length is a share of: the first step, or the step before it. */
export type FunnelBase = 'first' | 'previous';

export interface FunnelProps {
  steps: readonly FunnelStep[];
  /** Spoken name of the whole funnel. */
  label: string;
  renderLink?: (href: string, children: ReactNode) => ReactNode;
  format?: (n: number) => string;
  /** The bars' base; `previous` keeps the late steps readable when the first is large. */
  base?: FunnelBase;
  /** What sits between step i - 1 and step i (i from 1): the conversion, the drop-off, the time. */
  between?: (i: number) => ReactNode;
  /** The period before in words, for the outline's title: "the 4 weeks before". */
  previousLabel?: string;
}

/** 0..1 of `base`, clamped; nothing to measure against is zero. */
export function shareOf(value: number, base: number): number {
  if (!(base > 0)) return 0;
  return Math.min(1, Math.max(0, value / base));
}

/** The bar's and the outline's lengths for step i, each period against its own base. */
export function funnelLengths(
  steps: readonly FunnelStep[],
  i: number,
  base: FunnelBase,
): { now: number; before: number | undefined } {
  const step = steps[i]!;
  const against = base === 'first' || i === 0 ? steps[0]! : steps[i - 1]!;
  return {
    now: shareOf(step.value, against.value),
    before: step.previous === undefined ? undefined : shareOf(step.previous, against.previous ?? 0),
  };
}

const pct = (n: number) => `${Math.round(n * 100)}%`;

interface RowProps {
  step: FunnelStep;
  lengths: { now: number; before: number | undefined };
  props: FunnelProps;
}

function Row({ step, lengths, props }: RowProps): ReactNode {
  const format = props.format ?? String;
  const before = props.previousLabel ?? 'the period before';
  const title =
    step.previous === undefined
      ? `${step.label}: ${format(step.value)}`
      : `${step.label}: ${format(step.value)} (${pct(lengths.now)}); ${before} ${format(step.previous)}`;
  return (
    <div className="pui-funnel__row">
      <span className="pui-funnel__name">
        {step.href && props.renderLink ? props.renderLink(step.href, step.label) : step.label}
      </span>
      <span className="pui-funnel__track" title={title} aria-hidden="true">
        {lengths.before !== undefined ? (
          <span className="pui-funnel__ghost" style={{ width: pct(lengths.before) }} />
        ) : null}
        <span className="pui-funnel__bar" style={{ width: pct(lengths.now) }} />
      </span>
      <span className="pui-funnel__value">
        <strong>{format(step.value)}</strong> · {pct(lengths.now)}
        {step.previous !== undefined ? (
          <span className="pui-funnel__before"> ({format(step.previous)} before)</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * The standard funnel (round 3, dataviz M4): one row per step, a horizontal bar in the series blue
 * whose length is the step's share of the first step (or of the step before), the period before as
 * an outline behind it that stays visible when the two are equal, the count and share written out,
 * and what happened between two steps on a line of its own. HTML, so every number is text and every
 * step's name can be a link. Nothing for no steps.
 */
export function Funnel(props: FunnelProps): ReactNode {
  const { steps, label, base = 'first', between } = props;
  if (steps.length === 0) return null;
  return (
    <div className="pui-funnel" role="list" aria-label={label}>
      {steps.map((step, i) => (
        <div key={step.label} className="pui-funnel__step" role="listitem">
          {i > 0 && between ? <div className="pui-funnel__between">{between(i)}</div> : null}
          <Row step={step} lengths={funnelLengths(steps, i, base)} props={props} />
        </div>
      ))}
    </div>
  );
}
